import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Lock,
  Crown,
  Building2,
  ArrowRight,
  UserPlus,
  MessageSquare,
  PenTool,
  Landmark,
  Mail,
  Phone,
  FileText,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  Smartphone,
  BarChart3,
} from "lucide-react";
import { useUserDataContext } from "../../context/UserDataContext";
import UpgradeGateModal from "./UpgradeGateModal";

interface FeatureGateProps {
  requiredPlan: "pro" | "elite";
  featureName: string;
  children: React.ReactNode;
}

const TIER_LEVEL: Record<string, number> = {
  "free trial": 0,
  basic: 1,
  pro: 2,
  elite: 3,
};

const PLAN_DISPLAY: Record<string, { name: string; badge: string; badgeColor: string }> = {
  pro: { name: "Professional", badge: "PRO", badgeColor: "bg-emerald-600" },
  elite: { name: "Firm", badge: "FIRM", badgeColor: "bg-purple-600" },
};

// ── Preview Components ────────────────────────────────────────────

const LeadsPreview: React.FC = () => {
  const stages = [
    { name: "New", color: "#3b82f6", count: 3 },
    { name: "Contacted", color: "#f59e0b", count: 2 },
    { name: "Qualified", color: "#8b5cf6", count: 2 },
    { name: "Consultation", color: "#10b981", count: 1 },
  ];

  const sampleLeads: Record<string, { name: string; email: string; source: string; date: string }[]> = {
    New: [
      { name: "Sarah Chen", email: "sarah.chen@email.com", source: "Website", date: "Mar 21" },
      { name: "Marcus Rodriguez", email: "m.rodriguez@corp.com", source: "Referral", date: "Mar 20" },
      { name: "Emily Watson", email: "e.watson@gmail.com", source: "Phone", date: "Mar 19" },
    ],
    Contacted: [
      { name: "James Park", email: "j.park@startup.io", source: "Website", date: "Mar 18" },
      { name: "Lisa Thompson", email: "lisa.t@company.com", source: "Referral", date: "Mar 17" },
    ],
    Qualified: [
      { name: "David Kim", email: "d.kim@enterprise.ca", source: "LinkedIn", date: "Mar 16" },
      { name: "Anna Volkov", email: "anna.v@email.com", source: "Website", date: "Mar 15" },
    ],
    Consultation: [
      { name: "Robert Chang", email: "r.chang@legal.ca", source: "Referral", date: "Mar 14" },
    ],
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 px-6">
      {stages.map((stage) => (
        <div key={stage.name} className="flex-shrink-0 w-72">
          <div className="flex items-center gap-2 mb-3 px-1">
            <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
            <span className="text-sm font-semibold text-slate-700">{stage.name}</span>
            <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              {stage.count}
            </span>
          </div>
          <div className="space-y-2.5">
            {(sampleLeads[stage.name] || []).map((lead, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-2.5">
                  <div
                    className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: "#198754" }}
                  >
                    {lead.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{lead.name}</p>
                    <p className="text-xs text-slate-400 truncate">{lead.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full">
                    {lead.source}
                  </span>
                  <span className="text-xs text-slate-400">{lead.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CommunicationsPreview: React.FC = () => {
  const conversations = [
    { name: "Maria Santos", channel: "SMS", preview: "Thank you for the update on my case. I'll review the documents tonight.", time: "2m ago", unread: 2, icon: Smartphone, color: "#0F5132", bg: "#E8F5E9" },
    { name: "James Chen", channel: "Email", preview: "Attached are the signed settlement documents for your review.", time: "15m ago", unread: 0, icon: Mail, color: "#1e40af", bg: "#dbeafe" },
    { name: "Sarah Johnson", channel: "Voice", preview: "Incoming call, linked to Matter #2847", time: "1h ago", unread: 0, icon: Phone, color: "#7c3aed", bg: "#ede9fe" },
    { name: "Robert Kim", channel: "SMS", preview: "Can we reschedule our meeting to Thursday?", time: "3h ago", unread: 1, icon: Smartphone, color: "#0F5132", bg: "#E8F5E9" },
    { name: "Lisa Park", channel: "Email", preview: "Re: Immigration Application - I have a question about the supporting documents", time: "5h ago", unread: 0, icon: Mail, color: "#1e40af", bg: "#dbeafe" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 h-[400px] border border-slate-200 rounded-xl overflow-hidden mx-6">
      {/* Conversation list */}
      <div className="border-r border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-400">
            Search conversations...
          </div>
        </div>
        <div className="divide-y divide-slate-50">
          {conversations.map((conv, i) => {
            const Icon = conv.icon;
            return (
              <div
                key={i}
                className={`px-4 py-3.5 flex items-start gap-3 ${i === 0 ? "bg-emerald-50/50" : "hover:bg-slate-50"}`}
              >
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: conv.bg }}
                >
                  <Icon className="h-4 w-4" style={{ color: conv.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-semibold text-slate-800 truncate">{conv.name}</p>
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{conv.time}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{conv.preview}</p>
                </div>
                {conv.unread > 0 && (
                  <span
                    className="h-5 w-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-1"
                    style={{ backgroundColor: "#198754" }}
                  >
                    {conv.unread}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Message thread */}
      <div className="col-span-2 bg-slate-50 flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
          <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#E8F5E9" }}>
            <Smartphone className="h-4 w-4" style={{ color: "#0F5132" }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Maria Santos</p>
            <p className="text-xs text-slate-400">SMS, +1 (555) 234-5678</p>
          </div>
        </div>
        <div className="flex-1 p-5 space-y-3 overflow-hidden">
          <div className="flex justify-end">
            <div className="max-w-[70%] bg-emerald-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm">
              Hi Maria, just a quick update on your case. We received the opposing counsel's response today.
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-[70%] bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-slate-700">
              Thank you for the update on my case. I'll review the documents tonight.
            </div>
          </div>
          <div className="flex justify-end">
            <div className="max-w-[70%] bg-emerald-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm">
              Sounds good. Let me know if you have any questions.
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-white">
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-400">
            Type a message...
          </div>
        </div>
      </div>
    </div>
  );
};

const EsignPreview: React.FC = () => {
  const stats = [
    { label: "Sent This Month", value: "12", icon: FileText, color: "#1e40af", bg: "#dbeafe" },
    { label: "Completion Rate", value: "94%", icon: CheckCircle, color: "#0F5132", bg: "#E8F5E9" },
    { label: "Avg. Turnaround", value: "1.5 days", icon: Clock, color: "#7c3aed", bg: "#ede9fe" },
    { label: "Pending", value: "3", icon: Clock, color: "#d97706", bg: "#fef3c7" },
  ];

  const envelopes = [
    { status: "Signed", statusColor: "bg-emerald-100 text-emerald-700", subject: "Settlement Agreement, Smith v. Johnson", signers: "2/2 signed", date: "Mar 21" },
    { status: "Viewed", statusColor: "bg-blue-100 text-blue-700", subject: "Retainer Agreement, Chen Immigration", signers: "0/1 signed", date: "Mar 20" },
    { status: "Sent", statusColor: "bg-amber-100 text-amber-700", subject: "Purchase Agreement, 123 Oak Street", signers: "1/3 signed", date: "Mar 19" },
    { status: "Draft", statusColor: "bg-slate-100 text-slate-600", subject: "Power of Attorney, Martinez Estate", signers: "0/2 signed", date: "Mar 18" },
  ];

  return (
    <div className="px-6 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.bg }}>
                  <Icon className="h-4 w-4" style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-700">Recent Requests</p>
          <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-400">
            Search envelopes...
          </div>
        </div>
        <div className="divide-y divide-slate-50">
          {envelopes.map((env, i) => (
            <div key={i} className="px-5 py-3.5 flex items-center gap-4">
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${env.statusColor}`}>
                {env.status}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{env.subject}</p>
                <p className="text-xs text-slate-400">{env.signers}</p>
              </div>
              <span className="text-xs text-slate-400 flex-shrink-0">{env.date}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AccountingPreview: React.FC = () => {
  const stats = [
    { label: "Total Assets", value: "$2,450,000", icon: DollarSign, color: "#0F5132", bg: "#E8F5E9" },
    { label: "Total Revenue", value: "$485,200", icon: TrendingUp, color: "#1e40af", bg: "#dbeafe" },
    { label: "Total Expenses", value: "$142,800", icon: BarChart3, color: "#dc2626", bg: "#fee2e2" },
    { label: "Trust Balance", value: "$133,050", icon: Landmark, color: "#7c3aed", bg: "#ede9fe" },
  ];

  const transactions = [
    { date: "Mar 21", description: "Client retainer deposit, Martinez", account: "Trust Account, RBC", amount: "+$5,000.00", positive: true },
    { date: "Mar 20", description: "Office lease payment", account: "Operating, TD Bank", amount: "-$3,200.00", positive: false },
    { date: "Mar 19", description: "Invoice payment received, Chen", account: "Operating, TD Bank", amount: "+$8,750.00", positive: true },
    { date: "Mar 18", description: "Court filing fees", account: "Operating, TD Bank", amount: "-$450.00", positive: false },
    { date: "Mar 17", description: "Trust disbursement, Smith v. Johnson", account: "Trust Account, RBC", amount: "-$12,500.00", positive: false },
  ];

  return (
    <div className="px-6 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.bg }}>
                  <Icon className="h-4 w-4" style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-sm font-semibold text-slate-700">Recent Transactions</p>
        </div>
        <div className="divide-y divide-slate-50">
          {transactions.map((tx, i) => (
            <div key={i} className="px-5 py-3.5 flex items-center gap-4">
              <span className="text-xs text-slate-400 w-14 flex-shrink-0">{tx.date}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{tx.description}</p>
                <p className="text-xs text-slate-400">{tx.account}</p>
              </div>
              <span className={`text-sm font-bold flex-shrink-0 ${tx.positive ? "text-emerald-600" : "text-red-500"}`}>
                {tx.amount}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Feature config ────────────────────────────────────────────────

const FEATURE_CONFIG: Record<
  string,
  { icon: React.FC<any>; description: string; preview: React.FC }
> = {
  "Leads & Intake": {
    icon: UserPlus,
    description: "Kanban pipeline for new leads with intake forms, follow-up tracking, and one-click conversion to clients.",
    preview: LeadsPreview,
  },
  "Communications": {
    icon: MessageSquare,
    description: "Send and receive SMS, email, and voice calls directly from Kavoa, all linked to the right client and matter.",
    preview: CommunicationsPreview,
  },
  "E-Signatures": {
    icon: PenTool,
    description: "Send documents for signature, track status, and store signed copies automatically in the matter file.",
    preview: EsignPreview,
  },
  "Accounting": {
    icon: Landmark,
    description: "Trust accounting, operating accounts, general ledger, and financial statements, all compliant and integrated.",
    preview: AccountingPreview,
  },
};

// ── Main Component ────────────────────────────────────────────────

const FeatureGate: React.FC<FeatureGateProps> = ({
  requiredPlan,
  featureName,
  children,
}) => {
  const { firmData } = useUserDataContext();
  const [modalOpen, setModalOpen] = useState(false);

  const currentPlan = firmData?.subscriptionStatus || "free trial";
  const isOnTrial = firmData?.isOnTrial;
  const hasAccess =
    isOnTrial || (TIER_LEVEL[currentPlan] ?? 0) >= (TIER_LEVEL[requiredPlan] ?? 0);

  // If user has access, render children normally
  if (hasAccess) return <>{children}</>;

  const config = FEATURE_CONFIG[featureName] || {
    icon: Lock,
    description: "This feature requires a higher plan.",
    preview: () => null,
  };
  const FeatureIcon = config.icon;
  const PreviewComponent = config.preview;
  const planDisplay = PLAN_DISPLAY[requiredPlan];

  return (
    <>
      <div className="relative min-h-[calc(100vh-64px)]">
        {/* Header */}
        <div className="px-6 pt-8 pb-6">
          <div className="flex items-center gap-3 mb-1">
            <FeatureIcon className="h-6 w-6" style={{ color: "#0F5132" }} />
            <h1 className="text-2xl font-bold text-slate-800">{featureName}</h1>
            <span className={`${planDisplay.badgeColor} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>
              {planDisplay.badge}
            </span>
          </div>
          <p className="text-sm text-slate-500 ml-9">{config.description}</p>
        </div>

        {/* Preview content */}
        <div className="select-none">
          <PreviewComponent />
        </div>

        {/* Gradient overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, transparent 0%, transparent 30%, rgba(248,250,252,0.4) 50%, rgba(248,250,252,0.85) 70%, rgba(248,250,252,0.98) 100%)",
          }}
        />

        {/* Clickable overlay to intercept all interactions */}
        <div
          className="absolute inset-0 cursor-pointer z-10"
          onClick={() => setModalOpen(true)}
        />

        {/* Floating upgrade card */}
        <div className="absolute inset-x-0 bottom-0 flex justify-center pb-16 z-20 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-8 max-w-md w-full mx-6 pointer-events-auto text-center"
          >
            <div
              className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-5"
              style={{ backgroundColor: "#E8F5E9" }}
            >
              <Lock className="h-7 w-7" style={{ color: "#0F5132" }} />
            </div>

            <h3 className="text-xl font-bold text-slate-800 mb-2">
              Unlock {featureName}
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Upgrade to the{" "}
              <span className="font-semibold text-slate-700">
                {planDisplay.name}
              </span>{" "}
              plan to access {featureName.toLowerCase()} and more.
            </p>

            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#146C43";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#0F5132";
              }}
            >
              See Upgrade Options
              <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      </div>

      <UpgradeGateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        featureName={featureName}
        requiredPlan={requiredPlan}
      />
    </>
  );
};

export default FeatureGate;
